import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { toSignal } from '@angular/core/rxjs-interop';
import { TooltipTriggerDirective } from '@group24ag/flowkit';
import { SidebarItemStore } from '@core/components/sidebar/components/sidebar-item/sidebar-item.store';
import { SidebarService } from '@core/components/sidebar/service/sidebar.service';
import { getLinkItemClasses } from '@core/components/sidebar/components/sidebar-item/sidebar-item.component';

@Component({
  selector: 'app-sidebar-item-collapsed-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    FontAwesomeModule,
    RouterLink,
    RouterLinkActive,
    TooltipTriggerDirective,
  ],
  template: `
    <a
      flkTooltipTrigger
      routerLinkActive
      class="not-highlightable group flex cursor-pointer items-center rounded-lg border p-2"
      [ngClass]="itemLinkClasses"
      routerLink="/{{ mainPath() }}"
      [tooltip]="hasSubPages() ? '' : mainLabel()"
      [position]="'right'"
      [showDelay]="600"
      [routerLinkActiveOptions]="{ exact: false }"
      (isActiveChange)="updateActiveRoute(mainPath(), $event)">
      <fa-icon class="size-6" [ngClass]="iconClasses" [icon]="icon()"></fa-icon>
    </a>
  `,
})
export class SidebarItemCollapsedIconComponent {
  /** INJECTIONS **/
  /****/
  private readonly store = inject(SidebarItemStore);

  /****/
  private readonly service = inject(SidebarService);

  /** PROPERTIES **/
  /****/
  protected readonly icon = this.store.icon;

  /****/
  protected readonly mainPath = this.store.mainPath;

  /****/
  protected readonly mainLabel = this.store.mainLabel;

  /****/
  protected readonly hasSubPages = computed(() => !!this.store.subPages());

  /****/
  private readonly _activeRoute = toSignal(this.service.routeChanged$);

  /** Active if any subpage matches (expandable) or the main path matches (simple) */
  protected readonly isActive = computed(() => {
    const subPages = this.store.subPages();
    return subPages
      ? subPages.some(page => page.path === this._activeRoute())
      : this._activeRoute() === this.mainPath();
  });

  protected get itemLinkClasses() {
    return getLinkItemClasses(this.isActive());
  }

  protected get iconClasses() {
    return this.isActive()
      ? 'text-greenish-500'
      : 'group-hover:text-greenish-500';
  }

  protected updateActiveRoute(route: string, active: boolean) {
    this.service.setActiveRoute(route, active);
  }
}
